import GeneralUtils from "./GeneralUtils";
import { NOTCH_POSITIONS } from "./components/wafer-control-map/wafer-control-map";
import { DieType } from "components/utility-component/wafer-map-widget/wafer-map/web-gl-utils/Types";

// eslint-disable-next-line @typescript-eslint/naming-convention
interface interfacesForThisComponent {
  hideDashboardBasedOnParams: (e: string) => boolean;
  // rotatingToken : (e:string) =>
}

// eslint-disable-next-line operator-linebreak
const hideDashboardBasedOnParams: interfacesForThisComponent["hideDashboardBasedOnParams"] =
  (search) => {
    const searchParams = new URLSearchParams(search);
    const isDashboardParam = searchParams.get("isDashboard")?.toLowerCase();

    // It will hide the Top_bar and Dashboard URL Example is below
    //  http://localhost:3000/bin-plus-tables?isDashboard=false

    return !(isDashboardParam === "false");
  };

const rotatingToken = (token: string) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-expressions
  token !== null && GeneralUtils.changeUserDataCookie("AUTH_TOKEN", token);
};

/**
 * Normalizes a die type name by converting to lowercase, trimming whitespace,
 * removing special characters, and removing trailing "Die" suffix.
 * @param name - Die type name to normalize
 * @returns Normalized die type name
 */
const normalizeDieTypeName = (name: string): string => {
  return name.toLowerCase()
    .trim()
    .replace(/\s+/g, ' ') // Normalize multiple spaces
    .replace(/[\/\-_]/g, '') // Remove special characters
    .replace(/\s+die\s*$/i, '') // Remove trailing "Die"
    .trim();
};

/**
 * Maps flatProbed angle value to notch position string.
 * flatProbed values: 0=BOTTOM/DOWN, 90=RIGHT, 180=TOP/UP, 270=LEFT
 * @param flatProbed - Flat probed angle value (0, 90, 180, or 270)
 * @returns Notch position string ('DOWN', 'UP', 'LEFT', or 'RIGHT')
 */
const mapFlatProbedToNotchPosition = (flatProbed: number): string => {
  // NOTCH_POSITIONS = [['UNKNOWN', 'Unknown'], ['DOWN', 'Down'], ['UP', 'Up'], ['LEFT', 'Left'], ['RIGHT', 'Right']]
  // 0=BOTTOM/DOWN, 90=RIGHT, 180=TOP/UP, 270=LEFT
  if (flatProbed === 0) {
    return NOTCH_POSITIONS[1][0]; // 'DOWN'
  } else if (flatProbed === 90) {
    return NOTCH_POSITIONS[4][0]; // 'RIGHT'
  } else if (flatProbed === 180) {
    return NOTCH_POSITIONS[2][0]; // 'UP'
  } else if (flatProbed === 270) {
    return NOTCH_POSITIONS[3][0]; // 'LEFT'
  }
  // Default to DOWN if value doesn't match
  return NOTCH_POSITIONS[1][0];
};

// Module-level cache for die type matching results to avoid duplicate lookups
let dieTypeMatchCache: Record<string, { found: DieType | null; logged: boolean }> = {};

/**
 * Clears the die type match cache. Should be called before processing a new batch of die types.
 */
const clearDieTypeMatchCache = (): void => {
  dieTypeMatchCache = {};
};

/**
 * Finds a die type by name using multiple matching strategies.
 * Uses caching to avoid duplicate lookups.
 * @param searchName - The die type name to search for
 * @param dieTypes - Array of available die types
 * @returns The found DieType object or null if not found
 */
const findDieTypeByName = (searchName: string, dieTypes: DieType[]): DieType | null => {
  if (!searchName || !dieTypes || dieTypes.length === 0) {
    return null;
  }

  const searchNameTrimmed = searchName.trim();
  
  // Check cache first
  if (dieTypeMatchCache[searchNameTrimmed]) {
    return dieTypeMatchCache[searchNameTrimmed].found;
  }

  const normalizedSearch = normalizeDieTypeName(searchNameTrimmed);
  
  // Strategy 1: Exact case-insensitive match (most common case)
  let found = dieTypes.find((dt: DieType) => 
    dt.name.toLowerCase().trim() === searchNameTrimmed.toLowerCase()
  );
  if (found) {
    dieTypeMatchCache[searchNameTrimmed] = { found, logged: false };
    return found;
  }

  // Strategy 2: Normalized exact match (removes "Die" suffix, special chars)
  found = dieTypes.find((dt: DieType) => 
    normalizeDieTypeName(dt.name) === normalizedSearch
  );
  if (found) {
    dieTypeMatchCache[searchNameTrimmed] = { found, logged: false };
    return found;
  }

  // Strategy 3: One contains the other (after normalization)
  found = dieTypes.find((dt: DieType) => {
    const normalizedDt = normalizeDieTypeName(dt.name);
    return normalizedDt === normalizedSearch ||
           normalizedDt.includes(normalizedSearch) ||
           normalizedSearch.includes(normalizedDt);
  });
  if (found) {
    dieTypeMatchCache[searchNameTrimmed] = { found, logged: false };
    return found;
  }

  // Strategy 4: Word-by-word matching (for "Mirror Die" vs "Mirror")
  const searchWords = normalizedSearch.split(/\s+/).filter(w => w && w !== 'die');
  if (searchWords.length > 0) {
    found = dieTypes.find((dt: DieType) => {
      const dtWords = normalizeDieTypeName(dt.name).split(/\s+/).filter(w => w && w !== 'die');
      return searchWords.every(word => dtWords.includes(word)) ||
             dtWords.every(word => searchWords.includes(word));
    });
    if (found) {
      dieTypeMatchCache[searchNameTrimmed] = { found, logged: false };
      return found;
    }
  }

  // Not found - cache the result
  dieTypeMatchCache[searchNameTrimmed] = { found: null, logged: false };
  return null;
};

/**
 * Finds the closest standard wafer diameter from imported wafer size.
 * Imported WAFER SIZE values are in inches.
 * @param waferSize - Wafer size value from file (in inches)
 * @param outputUnit - 'MILLIMETERS' or 'INCHES' (default: 'INCHES')
 * @returns The closest standard diameter in the specified unit
 */
const findClosestWaferDiameter = (
  waferSize: number | string,
  outputUnit: 'MILLIMETERS' | 'INCHES' = 'INCHES',
): number => {
  const waferSizeInInches = Number(waferSize);
  if (!Number.isFinite(waferSizeInInches) || waferSizeInInches <= 0) {
    return outputUnit === 'INCHES' ? 8 : 200;
  }

  const availableDiametersInInches = [3, 4, 6, 8, 12];

  let closestDiameterInInches = availableDiametersInInches[0];
  let minDifference = Math.abs(waferSizeInInches - availableDiametersInInches[0]);
  for (let i = 1; i < availableDiametersInInches.length; i++) {
    const difference = Math.abs(waferSizeInInches - availableDiametersInInches[i]);
    if (difference < minDifference) {
      minDifference = difference;
      closestDiameterInInches = availableDiametersInInches[i];
    }
  }

  if (outputUnit === 'INCHES') {
    return closestDiameterInInches;
  }

  return Math.round(closestDiameterInInches * 25.4);
};

const normalizeAttributeEntityType = (v: any): string =>
  String(v ?? "").trim().toUpperCase();

/**
 * True if WCM (or similar) attribute metas already include this columnName + entityType pair.
 * Used before creating a new UDA via + UDA.
 */
const duplicateWcmAttributeMetaExists = (
  columnName: string,
  entityType: string,
  allMetas: any[],
): boolean => {
  const nameNorm = String(columnName ?? "").trim().toLowerCase();
  const entityNorm = normalizeAttributeEntityType(entityType);
  return allMetas.some(
    (m: any) =>
      String(m.columnName ?? "").trim().toLowerCase() === nameNorm &&
      normalizeAttributeEntityType(m.entityType) === entityNorm,
  );
};

/**
 * Checks if an attribute definition is a duplicate.
 * - When attribute metas include `entityType` (e.g. WCM): duplicate only if same columnName AND same entityType.
 * - Otherwise (e.g. Device Setup): uses definition row `level` keys vs overlapping entity types, or same name with no levels.
 */
const checkDuplicateAttribute = (
  newRowData: any,
  attributes: any[],
  allAttributes: any[],
  datagrid?: any,
): boolean => {
  if (!newRowData.name) {
    return false;
  }

  const getMeta = (attributeId: string) =>
    allAttributes.find((attr: any) => attr.id === attributeId);

  const newAttributeMeta = getMeta(newRowData.name);
  const newAttributeName = newAttributeMeta?.columnName;

  if (!newAttributeName) {
    return false;
  }

  const metaHasEntityType = (meta: any) =>
    meta?.entityType != null && String(meta.entityType).trim() !== "";

  const newMetaHasEntity = metaHasEntityType(newAttributeMeta);

  const newEntityTypes = newRowData.level
    ? Object.keys(newRowData.level).sort()
    : [];

  const isDuplicateRow = (existingRowData: any): boolean => {
    const existingMeta = getMeta(existingRowData.name);
    const existingAttributeName = existingMeta?.columnName;

    if (existingAttributeName !== newAttributeName) {
      return false;
    }

    const existingMetaHasEntity = metaHasEntityType(existingMeta);

    if (newMetaHasEntity && existingMetaHasEntity) {
      return (
        normalizeAttributeEntityType(newAttributeMeta.entityType) ===
        normalizeAttributeEntityType(existingMeta.entityType)
      );
    }

    const existingEntityTypes = existingRowData.level
      ? Object.keys(existingRowData.level).sort()
      : [];

    if (newEntityTypes.length === 0 && existingEntityTypes.length === 0) {
      return true;
    }

    return newEntityTypes.some((entityType: string) =>
      existingEntityTypes.includes(entityType),
    );
  };
  
  // Check against saved rows from props
  const duplicateInSavedRows = attributes.some((attr: any) => isDuplicateRow(attr));
  if (duplicateInSavedRows) {
    return true;
  }
  
  // Check against unsaved rows in DataGrid (if available)
  if (datagrid) {
    const allRows = datagrid.instance.getVisibleRows();
    const duplicateInUnsavedRows = allRows.some((row: any) => {
      const existingRowData = row.data || {};
      
      // Skip rows that are new (don't have an id yet) or are currently being edited
      if (!existingRowData.id || row.isEditing || row.isNewRow) {
        return false;
      }
      
      // Skip rows that are already in saved attributes (to avoid double checking)
      if (attributes.some((attr: any) => attr.id === existingRowData.id)) {
        return false;
      }
      
      return isDuplicateRow(existingRowData);
    });
    
    if (duplicateInUnsavedRows) {
      return true;
    }
  }
  
  return false;
};

const commonFunctions = {
  hideDashboardBasedOnParams,
  rotatingToken,
  findClosestWaferDiameter,
  normalizeDieTypeName,
  mapFlatProbedToNotchPosition,
  findDieTypeByName,
  clearDieTypeMatchCache,
  checkDuplicateAttribute,
  duplicateWcmAttributeMetaExists,
};

export default commonFunctions;
